import _ from 'lodash';

import {
  DEFAULT_LOGIN_URL,
  DEFAULT_SIGN_UP_URL,
  SignupType,
} from '@zapier/auth';
import { ErrorActions, RouterSelectors } from '@zapier/toolbox-redux-framework';
import { addTruthyParamsToUrl, urlWithNext } from '@zapier/url-utils';
import { getJson, postJson } from '@zapier/toolbox-browser-fetch';

import { Profile } from '@zapier/shared-entities';
import AccountActions from 'app/redux-boneyard/account/AccountActions';
import ActionTypes from 'app/login/ActionTypes';
import Config from 'app/common/Config';
import Data from 'app/login/Data';
import TrackActions from 'app/track/TrackActions';
import TrackData from 'app/track/Data';
import z from 'app/common/z';
import { PLATFORM_LOGIN_URL } from 'app/login/constants';
import { isHighIntent } from 'app/onboarding/utils/highIntent';
import { isSigningUpFromBlog } from 'app/common/utils/HistoryUtils';
import { maybeUnsafeRouteOptions } from 'app/common/utils/RouterUtils';
import {
  is2FARequired,
  isLocked,
  isMissingCsrf,
  isSingleSessionRequired,
  isValidCode,
  isValidLogin,
} from 'app/login/submitResults';

const PLATFORM_PATH_RE = /^\/platform.+/;

const defaultBeforeLoadAccount = () => Promise.resolve();

const doLoginSideEffects = (
  dispatch,
  { beforeLoadAccount = defaultBeforeLoadAccount } = {},
  result
) => {
  if (isValidLogin(result) || isValidCode(result)) {
    return z.useful
      .refreshChrome({ onlyIfHasChrome: true })
      .then(() => {
        // See base.html for this goofiness.
        if (Config.loadPrivateResources) {
          Config.loadPrivateResources();
        }
        dispatch(ErrorActions.clearErrors());
        dispatch({ type: ActionTypes.RESET_SESSION_TIMED_OUT });
        // Maybe need to wait for these?
        // Also, some of these may be redundant, but it's hard to know when
        // they're needed or not. Really need a better fetch sub-system for
        // eliminating duplicates.
        dispatch(AccountActions.resetUserData());
        beforeLoadAccount().then(() => {
          dispatch(AccountActions.reloadAccount()).then(() => {
            dispatch(Profile.actions.identifyToFullStory());
          });
        });
      })
      .then(() => result);
  }
  return Promise.resolve(result);
};

const LoginActions = {
  updateForm(page, name, value) {
    return {
      type: ActionTypes.UPDATE_LOGIN_FORM,
      page,
      name,
      value,
    };
  },

  resetForm(page) {
    return {
      type: ActionTypes.RESET_LOGIN_FORM,
      page,
    };
  },

  resetResult(page) {
    return {
      type: ActionTypes.RESET_RESULT,
      page,
    };
  },

  redirectToLoginOrSignupPage({
    shouldPreferSignUp = false,
    replaceRoute = false,
  } = {}) {
    return (dispatch, getState) => {
      const state = getState();
      const nextUrl = RouterSelectors.currentUrl(state);
      const email = Profile.selectors.email(
        Profile.selectors.all.currentProfile(state)
      );
      const hasSessionTimedOut = !!email;

      const redirectUrl = urlWithNext(
        shouldPreferSignUp && !hasSessionTimedOut ? '/sign-up' : '/app/login',
        nextUrl
      );

      // If we're already on one of the `redirectUrl` pages, don't redirect again
      if (
        RouterSelectors.isCurrentUrlBlacklisted(state, [
          '/sign-up',
          '/app/login',
        ])
      ) {
        return undefined;
      }

      return dispatch({
        type: ActionTypes.SESSION_TIMED_OUT,
        routeTo: {
          path: redirectUrl,
          replace: replaceRoute,
        },
      });
    };
  },

  login(email, password, next) {
    return dispatch => {
      dispatch({
        type: ActionTypes.SUBMIT_LOGIN,
        page: 'loginPage',
        // .logout is really a no-op right now. We have to force a refresh on
        // logout anyway, because otherwise, there's too much backbone junk
        // laying around. Later, this could flush the store though.
        // eslint-disable-next-line no-async-promise-executor
        promise: new Promise(async (resolve, reject) => {
          try {
            await Data.logout();
            const {
              accountId,
              didRedirect,
            } = await LoginActions.maybeRedirectToSaml({
              email,
            });
            if (didRedirect) {
              return;
            }
            const result = await Data.login(email, password, accountId);
            doLoginSideEffects(dispatch, {}, result);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }),
        routeTo: ({ result }) => {
          if (isValidLogin(result.type)) {
            return maybeUnsafeRouteOptions({
              path: next,
              replace: true,
              state: {
                isFromLogin: true,
              },
            });
          } else if (isLocked(result.type)) {
            return '/app/login/locked';
          } else if (is2FARequired(result.type)) {
            return {
              path: urlWithNext('/app/login/2fa', next),
              replace: true,
            };
          } else if (isSingleSessionRequired(result.type)) {
            return {
              path: urlWithNext('/app/login/secure-session', next),
              replace: true,
            };
          } else if (isMissingCsrf(result.type)) {
            return '/app/login/cookies-disabled';
          }
          // Is it possible to reach this?
          return undefined;
        },
      });
    };
  },

  update2FAForm(code) {
    return {
      type: ActionTypes.UPDATE_LOGIN_FORM,
      code,
    };
  },

  confirm2FACode(type, code, next) {
    // type is either 'code' or 'recovery_code'
    return dispatch => {
      return dispatch({
        type: ActionTypes.SUBMIT_LOGIN,
        page: 'twoFactorAuthPage',
        promise: Data.confirm2FACode(type, code).then(
          doLoginSideEffects.bind(null, dispatch, {})
        ),
        routeTo: ({ result }) => {
          if (isValidCode(result)) {
            return maybeUnsafeRouteOptions({
              path: next || DEFAULT_LOGIN_URL,
              replace: true,
            });
          }
          return undefined;
        },
      });
    };
  },

  confirmSecureSession(code, next) {
    return dispatch => {
      return dispatch({
        type: ActionTypes.SUBMIT_LOGIN,
        page: 'secureSessionAuthPage',
        promise: Data.confirmSecureSession(code).then(
          doLoginSideEffects.bind(null, dispatch, {})
        ),
        routeTo: ({ result }) => {
          if (isValidCode(result)) {
            return maybeUnsafeRouteOptions({
              path: next || DEFAULT_LOGIN_URL,
              replace: true,
            });
          }
          return undefined;
        },
      });
    };
  },

  forgotPassword(email) {
    return {
      type: ActionTypes.SUBMIT_LOGIN,
      page: 'forgotPage',
      promise: Data.forgotPassword(email),
    };
  },

  resetPassword(username, code, password1, password2) {
    return dispatch => {
      return dispatch({
        type: ActionTypes.SUBMIT_LOGIN,
        page: 'resetPage',
        promise: Data.resetPassword(username, code, password1, password2),
      });
    };
  },

  maybeRedirectToSaml: async (data = {}) => {
    // Passwords shouldn't ever exist in `data`, but `omit` them
    // just to be sure since we'll add `data` to a query string.
    const cleanData = _.omit(data, [Profile.NEW_PASSWORD]);

    // Try to find the last account that the user logged in as
    const rawAccountId = Number.parseInt(
      localStorage.getItem('currentAccountId')
    );
    const storedAccountId =
      rawAccountId && Number.isInteger(rawAccountId) && rawAccountId > 0
        ? rawAccountId
        : null;
    let account;

    try {
      // Use account discovery to see if the user is trying to log into
      // an account that is SAML enabled
      const idpData = await getJson(
        addTruthyParamsToUrl('/api/v4/account-discovery/', {
          account_id: storedAccountId,
          email: cleanData.email,
        })
      );
      account = _.get(idpData, 'account');

      if (!account && storedAccountId) {
        // This means the email and storedAccountId didn't match
        // so try making the request again w/o the storedAccountId
        // so we can just let the email dictate which account we are
        // logging in as
        const noAccountData = await getJson(
          addTruthyParamsToUrl('/api/v4/account-discovery/', {
            email: cleanData.email,
          })
        );
        account = _.get(noAccountData, 'account', {});
      }

      // If the account we are trying to log in as requires SAML
      // then redirect the user to the SAML login flow
      if (account.has_saml_sso) {
        window.location = addTruthyParamsToUrl(account.sso_url, cleanData);
        return { accountId: account.id, didRedirect: true };
      }
    } catch (e) {
      // If this endpoint returns an error that's expected,
      // so ignore it. It just means that SAML isn't supported
      // for the submitted email address.
    }
    return { accountId: _.get(account, 'id'), didRedirect: false };
  },

  signUp(signUpData, next) {
    return (dispatch, getState) => {
      const selectedApis = (
        RouterSelectors.getQueryParam(getState(), 'selected_apis') || ''
      ).split(',');
      const useCaseSlug = RouterSelectors.getQueryParam(getState(), 'use_case');

      signUpData = {
        ...signUpData,
        invitation_code: RouterSelectors.getQueryParam(
          getState(),
          'invitation'
        ),
        referrer: RouterSelectors.getQueryParam(getState(), 'referrer'),
        selected_apis: selectedApis,
        use_case_slug: useCaseSlug,
      };

      TrackData.trackSignupIntent(SignupType.direct, {
        selectedApis,
        useCaseSlug,
      });

      dispatch({
        type: ActionTypes.SUBMIT_LOGIN,
        page: 'signUpPage',
        signUpData,
        // See comment in login action creator above.
        // eslint-disable-next-line no-async-promise-executor
        promise: new Promise(async (resolve, reject) => {
          try {
            await Data.logout();
            const { didRedirect } = await LoginActions.maybeRedirectToSaml(
              signUpData
            );
            if (didRedirect) {
              return;
            }
            await dispatch(Profile.actions.signUp(signUpData));
            const result = await Data.login(
              signUpData.email,
              signUpData.password1
            );
            if (!isValidLogin(result.type)) {
              throw new Error(
                'Something seems to have gone wrong there. Please try again.'
              );
            }
            dispatch(ErrorActions.clearErrors());
            await doLoginSideEffects(dispatch, {}, result);
            resolve(result);
          } catch (error) {
            if (error.fieldErrors) {
              resolve({ fieldErrors: error.fieldErrors });
            }
            reject(error);
          }
        }),
        // Only continue if there were no field errors and captcha verification is not required.
        routeTo: ({ result }, state) => {
          if (result && (result.fieldErrors || result.isCaptchaRequired)) {
            return undefined;
          }
          const invitationCode = RouterSelectors.getQueryParam(
            state,
            'invitation'
          );
          const nextParams = {
            selected_apis: selectedApis,
            ...(invitationCode && { invitation: invitationCode }),
          };
          const isPlatformPage = PLATFORM_PATH_RE.test(
            RouterSelectors.getCurrentPathname(state)
          );

          const extraParams = {
            ...(isSigningUpFromBlog() ? { previous: document.referrer } : {}),
          };

          const forwardBaseUrl = addTruthyParamsToUrl(
            isPlatformPage ? PLATFORM_LOGIN_URL : DEFAULT_SIGN_UP_URL,
            { ...nextParams, ...extraParams }
          );
          const forwardSignupUrl = isPlatformPage
            ? forwardBaseUrl
            : urlWithNext(forwardBaseUrl, next);

          const path = isHighIntent(getState()) ? next : forwardSignupUrl;

          return maybeUnsafeRouteOptions({
            path,
            replace: true,
            state: {
              isFromSignUp: true,
            },
          });
        },
      });
    };
  },

  attemptOneTapGoogleSignup(idToken, next) {
    return (dispatch, getState) => {
      const selectedApis = (
        RouterSelectors.getQueryParam(getState(), 'selected_apis') || ''
      ).split(',');
      const useCaseSlug = RouterSelectors.getQueryParam(getState(), 'use_case');

      TrackData.trackSignupIntent(SignupType.oneTap, {
        selectedApis,
        useCaseSlug,
      });

      dispatch(ErrorActions.clearErrors());
      dispatch({
        type: ActionTypes.GOOGLE_ONETAP_SIGNUP,
        idToken,
        formatError: error => ({ error }),
        promise: dispatch(
          Profile.actions.googleOneTapSignup(idToken, next)
        ).catch(errors => {
          if (errors) {
            TrackActions.track('google yolo iframe signup failed', {
              errors,
            });
          }
        }),
      });
    };
  },

  maybeReauthenticate: async () => {
    const data = await postJson('/api/v4/confirm-authentication/');
    const authenticateUrl = _.get(data, 'url');
    const authenticationToken = _.get(data, 'token');
    if (authenticateUrl && authenticationToken) {
      window.location = addTruthyParamsToUrl(authenticateUrl);
      return { authenticationToken };
    }
    return {};
  },

  reauthenticate() {
    return dispatch => {
      LoginActions.maybeReauthenticate().then(result => {
        return dispatch({
          type: ActionTypes.REAUTHENTICATE,
          token: result.authenticationToken,
        });
      });
    };
  },
};

export default LoginActions;
